import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import GlobalFooter from './components/GlobalFooter';

const PartnerNGOs = () => {
  const navigate = useNavigate();

  // NGO data for India (Delhi & Mumbai)
  // Total target: 1500 meals distributed across all NGOs (India: 1292, Singapore: 208)
  const indiaNGOs = [
    { id: 1, name: "The Hindu Women's Welfare Society", address: "Mumbai", city: "Mumbai", focus: "Women Empowerment", meals: 191, logo: "HW", emoji: "👩", logoImage: "https://customer-assets.emergentagent.com/job_bda09b9d-abb6-44f4-a2b2-1261c7bbed21/artifacts/q000tess_The%20Hindu%20Women%27s%20Welfare%20Society.png" },
    { id: 2, name: "War Widows Association", address: "Delhi", city: "Delhi", focus: "Widow Welfare", meals: 157, logo: "WW", emoji: "👩‍👧", logoImage: "https://customer-assets.emergentagent.com/job_bda09b9d-abb6-44f4-a2b2-1261c7bbed21/artifacts/k3ke7gee_War%20Widows%20Association.png" },
    { id: 3, name: "Nana Palkar Smruti Samiti", address: "Mumbai", city: "Mumbai", focus: "Social Welfare", meals: 146, logo: "NP", emoji: "🤝", logoImage: "/ngo-logos/nana-palkar-smruti-samiti.png" },
    { id: 4, name: "Blind Relief Association", address: "Delhi", city: "Delhi", focus: "Visually Impaired Support", meals: 174, logo: "BR", emoji: "👁️", logoImage: "https://customer-assets.emergentagent.com/job_bda09b9d-abb6-44f4-a2b2-1261c7bbed21/artifacts/hfy96q7i_Blind%20Relief%20Association.png" },
    { id: 5, name: "Rescue Foundation", address: "Mumbai", city: "Mumbai", focus: "Child Welfare", meals: 135, logo: "RF", emoji: "🛡️", logoImage: "https://customer-assets.emergentagent.com/job_bda09b9d-abb6-44f4-a2b2-1261c7bbed21/artifacts/h879y6yh_rescue%20foundation.png" },
    { id: 6, name: "Signal Shala", address: "Mumbai", city: "Mumbai", focus: "Education & Meals", meals: 163, logo: "SS", emoji: "📚", logoImage: "https://customer-assets.emergentagent.com/job_bda09b9d-abb6-44f4-a2b2-1261c7bbed21/artifacts/8qzar31s_signal%20shala.png" },
    { id: 7, name: "Mukh Dhwani Vidyalaya", address: "Mumbai", city: "Mumbai", focus: "Education & Development", meals: 152, logo: "MD", emoji: "🎓", logoImage: "/ngo-logos/mukh-dhwani-vidyalaya.png" },
    { id: 8, name: "Khushi Foundation", address: "Delhi", city: "Delhi", focus: "Child Development", meals: 174, logo: "KF", emoji: "😊", logoImage: "/ngo-logos/khushi-foundation.png" }
  ];

  // Real NGO data for Singapore
  // Singapore total: 208 meals
  const singaporeNGOs = [
    { id: 11, name: "ACMI", address: "Singapore", focus: "Community Support", meals: 32, logo: "AC", emoji: "🤝", logoImage: "/ngo-logos/acmi-sg.png" },
    { id: 12, name: "Krsna's Free Meals", address: "Singapore", focus: "Food Distribution", meals: 51, logo: "KM", emoji: "🍱", logoImage: "/ngo-logos/krsnas-sg.png" },
    { id: 13, name: "It's Raining Raincoats", address: "Singapore", focus: "Migrant Support", meals: 44, logo: "RR", emoji: "☔", logoImage: "/ngo-logos/raincoats-sg.png" },
    { id: 14, name: "Free Food For All (FFFA)", address: "Singapore", focus: "Hunger Relief", meals: 43, logo: "FF", emoji: "🍽️", logoImage: "/ngo-logos/fffa-sg.png" },
    { id: 15, name: "Singapore Red Cross", address: "Singapore", focus: "Humanitarian Aid", meals: 38, logo: "RC", emoji: "❤️", logoImage: "/ngo-logos/redcross-sg.png" }
  ];

  // Calculate statistics
  const indiaStats = {
    totalNGOs: indiaNGOs.length,
    totalMeals: indiaNGOs.reduce((sum, ngo) => sum + ngo.meals, 0),
    areas: [...new Set(indiaNGOs.map(ngo => ngo.city))].length
  };

  const singaporeStats = {
    totalNGOs: singaporeNGOs.length,
    totalMeals: singaporeNGOs.reduce((sum, ngo) => sum + ngo.meals, 0),
    areas: 1 // All Singapore NGOs are in Singapore
  };

  const NGOCard = ({ ngo }) => (
    <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg group">
      <CardContent className="p-6">
        <div className="flex flex-col items-center text-center space-y-4">
          {/* NGO Logo */}
          <div className="w-24 h-24 rounded-full flex items-center justify-center shadow-lg overflow-hidden bg-white border-2 border-gray-100">
            {ngo.logoImage ? (
              <img 
                src={ngo.logoImage} 
                alt={`${ngo.name} logo`}
                className="w-full h-full object-contain p-2"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                <span className="text-white font-bold text-2xl">{ngo.logo}</span>
              </div>
            )}
          </div>
          
          {/* NGO Name */}
          <CardTitle className="text-xl group-hover:text-green-600 transition-colors font-semibold">
            {ngo.name}
          </CardTitle>
          
          {/* Address */}
          <div className="flex items-center space-x-1 text-gray-600">
            <span className="text-lg">📍</span>
            <span className="text-base font-medium">{ngo.address || ngo.city}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Page Header */}
      <section 
        className="py-20 px-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/ngos-banner.png)' }}
      >
        {/* Background overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        
        <div className="container mx-auto px-4 max-w-6xl text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
            Our Partner <span className="text-green-400">NGOs</span>
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto leading-relaxed drop-shadow-md">
            Meet the organizations helping us deliver rescued food to those who need it most. 
            Together, we're building stronger communities across India and Singapore.
          </p>
        </div>
      </section>

      {/* NGO Listings */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <Tabs defaultValue="india" className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="india" className="text-lg py-3" data-testid="india-tab">
                  🇮🇳 India Partners
                </TabsTrigger>
                <TabsTrigger value="singapore" className="text-lg py-3" data-testid="singapore-tab">
                  🇸🇬 Singapore Partners
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="india" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">India Partners</h2>
                <div className="flex justify-center space-x-8 text-sm text-gray-600">
                  <div>
                    <span className="font-semibold text-green-600">{indiaStats.totalNGOs}</span> NGOs
                  </div>
                  <div>
                    <span className="font-semibold text-green-600">{indiaStats.totalMeals.toLocaleString()}</span> Meals Distributed
                  </div>
                  <div>
                    <span className="font-semibold text-green-600">{indiaStats.areas}</span> Cities
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {indiaNGOs.map(ngo => (
                  <NGOCard key={ngo.id} ngo={ngo} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="singapore" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Singapore Partners</h2>
                <div className="flex justify-center space-x-8 text-sm text-gray-600">
                  <div>
                    <span className="font-semibold text-green-600">{singaporeStats.totalNGOs}</span> NGOs
                  </div>
                  <div>
                    <span className="font-semibold text-green-600">{singaporeStats.totalMeals.toLocaleString()}</span> Meals Distributed
                  </div>
                  <div>
                    <span className="font-semibold text-green-600">{singaporeStats.areas}</span> Region
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {singaporeNGOs.map(ngo => (
                  <NGOCard key={ngo.id} ngo={ngo} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">
            Community Impact by Region
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* India Stats */}
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-6">
                <div className="text-6xl mb-4">🇮🇳</div>
                <CardTitle className="text-2xl">India Impact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{indiaStats.totalNGOs}</div>
                    <div className="text-sm text-gray-600">Partner NGOs</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{indiaStats.totalMeals.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Meals Distributed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{indiaStats.areas}</div>
                    <div className="text-sm text-gray-600">Cities Covered</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Singapore Stats */}
            <Card className="border-0 shadow-xl">
              <CardHeader className="text-center pb-6">
                <div className="text-6xl mb-4">🇸🇬</div>
                <CardTitle className="text-2xl">Singapore Impact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{singaporeStats.totalNGOs}</div>
                    <div className="text-sm text-gray-600">Partner NGOs</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{singaporeStats.totalMeals.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Meals Distributed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{singaporeStats.areas}</div>
                    <div className="text-sm text-gray-600">Region Covered</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Want to Collaborate with Us?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Join our network of partner NGOs and help us distribute rescued food to communities 
            in need. Together, we can make a greater impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-3"
              data-testid="ngo-signup-btn"
            >
              Join as an NGO Partner
            </Button>
          </div>
        </div>
      </section>

      {/* Global Footer */}
      <GlobalFooter />
    </div>
  );
};

export default PartnerNGOs;